import axios from '../helpers/axios'
import { cartConstants } from './constants'
import store from '../store'
import {
  getCartItemId,
  getOrderItemPrice,
  getOrderItemQuantity,
  getOrderItemSizeLabel,
  getOrderItemType,
  getOrderItemVariantKey,
  getOrderItemWorkType,
  getProductIdFromItem,
  normalizeCartItemMeta,
  normalizeItemsArray,
} from '../utils/orderItemMeta'
import {
  isCartItemConfirmationRequired,
  isCartItemConfirmed,
} from '../utils/cartConfirmation'

const toArrayCartItems = (cartItems) => normalizeItemsArray(cartItems)

const saveLocalCart = (cartItems) => {
  localStorage.setItem('cart', JSON.stringify(toArrayCartItems(cartItems)))
}

const CART_CLIENT_META_KEY = 'cart_configured_items_meta'

const readClientCartMeta = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(CART_CLIENT_META_KEY) || '{}')
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch (error) {
    return {}
  }
}

const saveClientCartMeta = (meta) => {
  try {
    localStorage.setItem(CART_CLIENT_META_KEY, JSON.stringify(meta || {}))
  } catch (error) {
    // localStorage can be unavailable in private mode. Cart must still work.
  }
}

const getProductIdFromAny = (item) => getProductIdFromItem(item)

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key)

const resolveCartConfirmationMeta = (item = {}, calculatorData = null) => {
  const incomingCalc = calculatorData && typeof calculatorData === 'object'
    ? calculatorData
    : null
  const mergedCalculatorData = {
    ...(item?.calculatorData && typeof item.calculatorData === 'object'
      ? item.calculatorData
      : {}),
    ...(incomingCalc || {}),
  }
  const source = {
    ...item,
    calculatorConfigured: item?.calculatorConfigured === true || mergedCalculatorData?.configured === true,
    calculatorData: mergedCalculatorData,
  }

  if (incomingCalc && hasOwn(incomingCalc, 'confirmationRequired') && typeof incomingCalc.confirmationRequired === 'boolean') {
    source.confirmationRequired = incomingCalc.confirmationRequired
  }

  if (incomingCalc && hasOwn(incomingCalc, 'itemConfirmed') && typeof incomingCalc.itemConfirmed === 'boolean') {
    source.itemConfirmed = incomingCalc.itemConfirmed
  }

  const confirmationRequired = isCartItemConfirmationRequired(source)
  const itemConfirmed = confirmationRequired
    ? isCartItemConfirmed(source)
    : source?.itemConfirmed === true || mergedCalculatorData?.itemConfirmed === true

  return {
    confirmationRequired,
    itemConfirmed,
    calculatorData: mergedCalculatorData,
  }
}

const normalizeOrderType = (calculatorData = {}, product = {}) => {
  const source = {
    ...product,
    calculatorData: {
      ...(product?.calculatorData || {}),
      ...(calculatorData || {}),
    },
  }

  return {
    orderItemType: getOrderItemType(source),
    workType: getOrderItemWorkType(source),
  }
}

const buildClientMetaKeys = (productId, calculatorData = {}) => {
  const normalizedProductId = String(productId || '')
  if (!normalizedProductId) return []

  const source = {
    productId: normalizedProductId,
    calculatorData,
  }
  const keys = []
  const variantKey = String(calculatorData?.variantKey || getOrderItemVariantKey(source) || '')
  const { orderItemType, workType } = normalizeOrderType(calculatorData, {})
  const sizeLabel = String(
    calculatorData?.sizeLabel ||
    calculatorData?.selectedSizeLabel ||
    calculatorData?.millingDimensionsLabel ||
    ''
  ).trim()

  if (variantKey) {
    keys.push(`${normalizedProductId}::variant::${variantKey}`)
  }

  if (sizeLabel) {
    keys.push(`${normalizedProductId}::type::${orderItemType}::${workType || 'whole'}::size::${sizeLabel}`)
  }

  keys.push(`${normalizedProductId}::type::${orderItemType}::${workType || 'whole'}`)
  keys.push(`${normalizedProductId}::last`)

  return Array.from(new Set(keys))
}

const getClientMetaCandidates = (productId, meta = {}) => {
  const prefix = `${String(productId || '')}::`

  if (!productId || !meta || typeof meta !== 'object') return []

  const candidates = Object.entries(meta)
    .filter(([key]) => String(key).startsWith(prefix))
    .map(([, value]) => value)
    .filter(Boolean)

  const unique = []
  const seen = new Set()

  candidates.forEach((candidate) => {
    const calc = candidate?.calculatorData || {}
    const signature = [
      candidate?.orderItemType,
      candidate?.workType,
      candidate?.workKey,
      calc?.variantKey,
      candidate?.sizeLabel || calc?.sizeLabel || calc?.selectedSizeLabel,
      candidate?.payablePrice || calc?.payablePrice,
    ].join('::')

    if (seen.has(signature)) return

    seen.add(signature)
    unique.push(candidate)
  })

  return unique
}

const getItemComparablePrice = (item = {}) => getOrderItemPrice(item)
const getMetaComparablePrice = (meta = {}) => getOrderItemPrice(meta)
const getItemComparableSize = (item = {}) => getOrderItemSizeLabel(item)
const getMetaComparableSize = (meta = {}) => getOrderItemSizeLabel(meta)

const findBestClientMetaForCartItem = (item = {}, meta = {}) => {
  const productId = getProductIdFromAny(item)
  const candidates = getClientMetaCandidates(productId, meta)

  if (!candidates.length) return null

  const itemOrderType = normalizeOrderType(item?.calculatorData || {}, item)
  const hasServerType = Boolean(
    item?.orderItemType ||
    item?.workType ||
    item?.workKey ||
    item?.cartItemType ||
    item?.orderItemKind ||
    item?.calculatorData?.orderItemType ||
    item?.calculatorData?.workType ||
    item?.calculatorData?.workKey
  )
  const itemPrice = getItemComparablePrice(item)
  const itemSize = getItemComparableSize(item)
  const itemVariantKey = getOrderItemVariantKey(item)

  const scored = candidates.map((candidate) => {
    const candidateOrderType = normalizeOrderType(candidate?.calculatorData || {}, candidate)
    const candidatePrice = getMetaComparablePrice(candidate)
    const candidateSize = getMetaComparableSize(candidate)
    const candidateVariantKey = getOrderItemVariantKey(candidate)
    let score = 0

    if (itemVariantKey && candidateVariantKey && itemVariantKey === candidateVariantKey) score += 30
    if (hasServerType && candidateOrderType.orderItemType === itemOrderType.orderItemType) score += 8
    if (hasServerType && candidateOrderType.workType === itemOrderType.workType) score += 6
    if (itemPrice > 0 && candidatePrice > 0 && Math.abs(itemPrice - candidatePrice) < 0.01) score += 12
    if (itemSize && candidateSize && itemSize === candidateSize) score += 10

    return { candidate, score }
  }).sort((a, b) => b.score - a.score)

  if (scored[0]?.score > 0) return scored[0].candidate
  if (candidates.length === 1) return candidates[0]

  return null
}

const rememberConfiguredCartItem = (product = {}, calculatorData = {}) => {
  if (!calculatorData || calculatorData?.configured !== true) return

  const productId = getProductIdFromAny(product)
  if (!productId) return

  const meta = readClientCartMeta()
  const normalizedMeta = {
    calculatorData,
    ...normalizeCartItemMeta(product, calculatorData),
  }

  buildClientMetaKeys(productId, calculatorData).forEach((key) => {
    meta[key] = normalizedMeta
  })

  saveClientCartMeta(meta)
}

const mergeClientMetaIntoCartItems = (items = []) => {
  const meta = readClientCartMeta()

  if (!meta || !Object.keys(meta).length) return toArrayCartItems(items)

  return toArrayCartItems(items).map((item) => {
    const calculatorData = item?.calculatorData || {}

    if (calculatorData?.configured === true && (calculatorData?.orderItemType || item?.orderItemType)) {
      return item
    }

    const productId = getProductIdFromAny(item)
    const keys = buildClientMetaKeys(productId, calculatorData)
    const foundMeta = keys.map((key) => meta[key]).find(Boolean) || findBestClientMetaForCartItem(item, meta)

    if (!foundMeta) return item

    const nextCalculatorData = {
      ...(foundMeta.calculatorData || {}),
      ...(item.calculatorData || {}),
      orderItemType: item?.calculatorData?.orderItemType || foundMeta.orderItemType,
      workType: item?.calculatorData?.workType || foundMeta.workType,
      workKey: item?.calculatorData?.workKey || foundMeta.workKey,
      workName: item?.calculatorData?.workName || foundMeta.workName,
      workLabel: item?.calculatorData?.workLabel || foundMeta.workLabel,
      measureMode: item?.calculatorData?.measureMode || foundMeta.measureMode,
      payablePrice: item?.calculatorData?.payablePrice || foundMeta.payablePrice,
      selectedPrice: item?.calculatorData?.selectedPrice || foundMeta.selectedPrice,
      productPrice: item?.calculatorData?.productPrice || foundMeta.productPrice,
      workPrice: item?.calculatorData?.workPrice || foundMeta.workPrice,
      weightKg: item?.calculatorData?.weightKg || foundMeta.weightKg,
      materialDensityKgM3: item?.calculatorData?.materialDensityKgM3 || foundMeta.materialDensityKgM3,
      stoneDensityKgM3: item?.calculatorData?.stoneDensityKgM3 || foundMeta.stoneDensityKgM3,
      selectedSizeKey: item?.calculatorData?.selectedSizeKey || foundMeta.selectedSizeKey,
      sizeLabel: item?.calculatorData?.sizeLabel || foundMeta.sizeLabel,
    }

    return {
      ...item,
      orderItemType: item?.orderItemType || foundMeta.orderItemType,
      workType: item?.workType || foundMeta.workType,
      workKey: item?.workKey || foundMeta.workKey,
      workName: item?.workName || foundMeta.workName,
      workLabel: item?.workLabel || foundMeta.workLabel,
      cartItemType: item?.cartItemType || foundMeta.cartItemType,
      orderItemKind: item?.orderItemKind || foundMeta.orderItemKind,
      orderItemTypeLabel: item?.orderItemTypeLabel || foundMeta.orderItemTypeLabel,
      categoryName: item?.categoryName || foundMeta.categoryName,
      categoryLabel: item?.categoryLabel || foundMeta.categoryLabel,
      sizeLabel: item?.sizeLabel || foundMeta.sizeLabel,
      selectedSizeKey: item?.selectedSizeKey || foundMeta.selectedSizeKey,
      variantKey: item?.variantKey || foundMeta.variantKey,
      measureMode: item?.measureMode || foundMeta.measureMode,
      payablePrice: item?.payablePrice || foundMeta.payablePrice,
      selectedPrice: item?.selectedPrice || foundMeta.selectedPrice,
      price: item?.price || foundMeta.price,
      productPrice: item?.productPrice || foundMeta.productPrice,
      productBasePrice: item?.productBasePrice || foundMeta.productBasePrice,
      workPrice: item?.workPrice || foundMeta.workPrice,
      millingPrice: item?.millingPrice || foundMeta.millingPrice,
      weightKg: item?.weightKg || foundMeta.weightKg,
      materialDensityKgM3: item?.materialDensityKgM3 || foundMeta.materialDensityKgM3,
      stoneDensityKgM3: item?.stoneDensityKgM3 || foundMeta.stoneDensityKgM3,
      millingDimensionsLabel: item?.millingDimensionsLabel || foundMeta.millingDimensionsLabel,
      millingPriceLabel: item?.millingPriceLabel || foundMeta.millingPriceLabel,
      calculatorConfigured: true,
      calculatorData: nextCalculatorData,
    }
  })
}

const getLocalCartItemKey = (item) => String(getCartItemId(item) || '')
const getLocalProductId = (item) => String(getProductIdFromAny(item) || '')
const getPayloadCartItemKey = (payload) => String(payload?.cartItemKey || payload?.cartItemId || payload?.variantKey || payload?.productId || '')
const isConfiguredItem = (calculatorData) => calculatorData?.configured === true

const getConfiguredSceneIdentity = (calculatorData = {}) => {
  const sceneInstanceId = String(
    calculatorData?.sceneInstanceId ||
    calculatorData?.sceneObjectSnapshot?.sceneInstanceId ||
    ''
  ).trim()
  if (sceneInstanceId) return `scene-instance:${sceneInstanceId}`

  const sceneObjectId = String(calculatorData?.sceneObjectId || '').trim()
  if (sceneObjectId) return `scene-object:${sceneObjectId}`

  const modelId = String(calculatorData?.modelId || calculatorData?.sceneModelId || '').trim()
  if (modelId) return `model:${modelId}`

  return ''
}

const getItemConfiguredSceneIdentity = (item = {}) => (
  getConfiguredSceneIdentity(item?.calculatorData || {})
)

const CART_SCOPE_GLOBAL = 'global'
const CART_SCOPE_SESSION = 'session'

const SESSION_CART_MUTATION_REQUEST = 'SESSION_CART_MUTATION_REQUEST'
const SESSION_CART_MUTATION_SUCCESS = 'SESSION_CART_MUTATION_SUCCESS'
const SESSION_CART_MUTATION_FAILURE = 'SESSION_CART_MUTATION_FAILURE'

const getVisibleProductId = (product = {}) => String(
  getProductIdFromAny(product) ||
  product?._id ||
  product?.id ||
  product?.productId ||
  product?.product ||
  ''
)

const getActiveSalesSessionIdentity = () => {
  const state = store.getState()
  const activeSession = state.officeSalesSession?.activeSession || null
  const officeId = String(
    activeSession?.officeId?._id ||
    activeSession?.officeId ||
    state.office?.currentOfficeId ||
    ''
  ).trim()
  const sessionId = String(activeSession?.sessionId || '').trim().toUpperCase()

  if (!officeId || officeId === 'all' || !sessionId) return null

  return {
    officeId,
    sessionId,
    activeSession,
  }
}

const normalizeCartOperationContext = (value = null) => {
  if (!value || typeof value !== 'object') return null

  const scope = value.scope === CART_SCOPE_SESSION
    ? CART_SCOPE_SESSION
    : CART_SCOPE_GLOBAL

  if (scope === CART_SCOPE_GLOBAL) {
    return {
      scope: CART_SCOPE_GLOBAL,
      operationId: String(value.operationId || ''),
    }
  }

  const officeId = String(value.officeId || '').trim()
  const sessionId = String(value.sessionId || '').trim().toUpperCase()

  if (!officeId || !sessionId) return null

  return {
    scope: CART_SCOPE_SESSION,
    officeId,
    sessionId,
    operationId: String(value.operationId || ''),
  }
}

const isSameSessionCartContext = (left = null, right = null) => {
  if (!left?.officeId || !left?.sessionId || !right?.officeId || !right?.sessionId) {
    return false
  }

  return String(left.officeId) === String(right.officeId) &&
    String(left.sessionId).toUpperCase() === String(right.sessionId).toUpperCase()
}

const resolveCartOperationContext = (explicitContext = null) => {
  const normalizedExplicit = normalizeCartOperationContext(explicitContext)
  if (normalizedExplicit) return normalizedExplicit

  const state = store.getState()
  const activeScope = state?.cart?.activeCartScope || CART_SCOPE_GLOBAL

  if (activeScope === CART_SCOPE_SESSION) {
    const identity = getActiveSalesSessionIdentity()

    if (identity) {
      return {
        scope: CART_SCOPE_SESSION,
        officeId: identity.officeId,
        sessionId: identity.sessionId,
        operationId: '',
      }
    }
  }

  return {
    scope: CART_SCOPE_GLOBAL,
    operationId: '',
  }
}

const getSessionCartItemsForContext = (cartContext) => {
  const normalizedContext = normalizeCartOperationContext(cartContext)
  if (!normalizedContext || normalizedContext.scope !== CART_SCOPE_SESSION) return null

  const state = store.getState()
  const storedContext = state?.cart?.sessionCartContext || null

  if (!isSameSessionCartContext(storedContext, normalizedContext)) return null

  return toArrayCartItems(state?.cart?.sessionCartItems || [])
}

const normalizeSalesSessionCartItem = (item = {}) => {
  const productId = getProductIdFromAny(item)
  if (!productId) return null

  const qty = getOrderItemQuantity(item)
  const price = getOrderItemPrice(item)
  const calculatorData = item?.calculatorData && typeof item.calculatorData === 'object'
    ? item.calculatorData
    : {}
  const confirmation = resolveCartConfirmationMeta(item, calculatorData)

  return {
    ...item,
    productId: String(productId),
    product: String(productId),
    qty,
    quantity: qty,
    purchasedQty: qty,
    payablePrice: price,
    price,
    selectedPrice: item?.selectedPrice ?? calculatorData?.selectedPrice ?? price,
    productName: item?.productName || item?.fullName || item?.name || calculatorData?.productName || '',
    name: item?.name || item?.productName || item?.fullName || '',
    fullName: item?.fullName || item?.productName || item?.name || '',
    categoryName: item?.categoryName || item?.categoryLabel || calculatorData?.categoryName || '',
    sizeLabel: getOrderItemSizeLabel(item) || calculatorData?.sizeLabel || calculatorData?.selectedSizeLabel || '',
    orderItemType: getOrderItemType(item),
    workType: getOrderItemWorkType(item),
    workKey: item?.workKey || calculatorData?.workKey || '',
    workName: item?.workName || calculatorData?.workName || '',
    workLabel: item?.workLabel || calculatorData?.workLabel || '',
    calculatorConfigured: item?.calculatorConfigured === true || calculatorData?.configured === true,
    confirmationRequired: confirmation.confirmationRequired,
    itemConfirmed: confirmation.itemConfirmed,
    calculatorData: {
      ...confirmation.calculatorData,
      configured: item?.calculatorConfigured === true || calculatorData?.configured === true,
      confirmationRequired: confirmation.confirmationRequired,
      itemConfirmed: confirmation.itemConfirmed,
      payablePrice: price,
      purchasedQty: qty,
      orderItemType: getOrderItemType(item),
      workType: getOrderItemWorkType(item),
      workKey: item?.workKey || calculatorData?.workKey || '',
      sizeLabel: getOrderItemSizeLabel(item) || calculatorData?.sizeLabel || calculatorData?.selectedSizeLabel || '',
    },
    cartItemId: getCartItemId(item),
    variantKey: getOrderItemVariantKey(item),
  }
}

const normalizeSalesSessionCartItems = (items = []) => {
  return toArrayCartItems(items)
    .map(normalizeSalesSessionCartItem)
    .filter(Boolean)
}

const buildSalesSessionCartTotals = (items = []) => {
  const normalized = normalizeSalesSessionCartItems(items)
  const subtotal = normalized.reduce((sum, item) => {
    return sum + (Number(item.payablePrice || 0) * Number(item.purchasedQty || item.qty || 1))
  }, 0)

  return {
    subtotal,
    discount: 0,
    total: subtotal,
    totalAmount: subtotal,
    itemsCount: normalized.length,
    totalQty: normalized.reduce((sum, item) => sum + (Number(item.purchasedQty || item.qty || 1) || 1), 0),
    updatedAt: new Date().toISOString(),
  }
}

const getCartTraceItem = (item = {}) => {
  const calc = item?.calculatorData || {}

  return {
    cartItemId: String(item?.cartItemId || item?.cartItemKey || ''),
    productId: String(getProductIdFromAny(item) || ''),
    modelId: String(calc?.modelId || ''),
    sceneObjectId: String(calc?.sceneObjectId || ''),
    variantKey: String(calc?.variantKey || item?.variantKey || ''),
    itemConfirmed: item?.itemConfirmed === true || calc?.itemConfirmed === true,
    qty: getOrderItemQuantity(item),
    sizeLabel: String(calc?.sizeLabel || item?.sizeLabel || ''),
    widthMm: calc?.widthMm ?? null,
    depthMm: calc?.depthMm ?? null,
    heightMm: calc?.heightMm ?? null,
    payablePrice: getOrderItemPrice(item),
    millingPrice: Number(calc?.millingPrice || 0) || 0,
    sceneDecorationsHash: String(calc?.sceneDecorationsHash || ''),
  }
}

const getSessionTraceItems = (session = {}) => {
  if (Array.isArray(session?.orderDraftState?.products)) return session.orderDraftState.products
  if (Array.isArray(session?.calc3dState?.cartItems)) return session.calc3dState.cartItems
  return []
}

const syncActiveSalesSessionCart = async (items = [], options = {}) => {
  const explicitContext = normalizeCartOperationContext(options?.cartContext)
  const activeIdentity = getActiveSalesSessionIdentity()
  const identity = explicitContext?.scope === CART_SCOPE_SESSION
    ? explicitContext
    : activeIdentity

  if (!identity?.officeId || !identity?.sessionId) return null

  const normalized = normalizeSalesSessionCartItems(items)
  const totals = buildSalesSessionCartTotals(normalized)
  const cartItemKey = String(options?.cartItemKey || '')

  console.log('[CART-TRACE][SESSION-SYNC][START]', {
    source: options.source || 'cart',
    officeId: identity.officeId,
    sessionId: identity.sessionId,
    cartItemKey,
    items: normalized.map(getCartTraceItem),
  })

  try {
    const res = await axios.patch(`/office-sales-session/${identity.officeId}/${identity.sessionId}/cart`, {
      items: normalized,
      totals,
      source: options.source || 'cart',
    })

    if (res.status === 200 && res.data?.session) {
      const returnedItems = getSessionTraceItems(res.data.session)

      console.log('[CART-TRACE][SESSION-SYNC][SUCCESS]', {
        source: options.source || 'cart',
        status: res.status,
        cartItemKey,
        sessionUpdatedAt: res.data.session?.updatedAt || null,
        items: returnedItems.map(getCartTraceItem),
      })

      store.dispatch({
        type: 'OFFICE_SALES_SESSION_CART_UPDATE_SUCCESS',
        payload: {
          session: res.data.session,
          officeId: identity.officeId,
          sessionId: identity.sessionId,
          cartContext: {
            scope: CART_SCOPE_SESSION,
            officeId: identity.officeId,
            sessionId: identity.sessionId,
            operationId: String(options?.cartContext?.operationId || ''),
          },
          message: res.data.message,
          silentAlert: options?.silentAlert === true,
          cartItemKey,
        },
      })
    }

    return res.data?.session || null
  } catch (error) {
    console.error('[CART-TRACE][SESSION-SYNC][FAIL]', {
      source: options.source || 'cart',
      cartItemKey,
      status: error?.response?.status || 0,
      message: error?.response?.data?.message || error?.response?.data?.error || error?.message || 'unknown',
    })

    store.dispatch({
      type: 'OFFICE_SALES_SESSION_CART_UPDATE_FAILURE',
      payload: {
        officeId: identity.officeId,
        sessionId: identity.sessionId,
        cartContext: {
          scope: CART_SCOPE_SESSION,
          officeId: identity.officeId,
          sessionId: identity.sessionId,
          operationId: String(options?.cartContext?.operationId || ''),
        },
        error: error?.response?.data?.message || error?.response?.data?.error || error.message,
        silentAlert: options?.silentAlert === true,
        cartItemKey,
      },
    })

    return null
  }
}


const shouldUseSalesSessionCart = (cartContext = null) => {
  return resolveCartOperationContext(cartContext).scope === CART_SCOPE_SESSION
}

export const setActiveCartScope = (scope = CART_SCOPE_GLOBAL) => ({
  type: 'SET_CART_ACTIVE_SCOPE',
  payload: scope === CART_SCOPE_SESSION ? CART_SCOPE_SESSION : CART_SCOPE_GLOBAL,
})

const buildLocalCartItem = (product = {}, qty = 1, normalizedCalculatorData = null) => {
  const productId = getVisibleProductId(product)
  const normalizedCartMeta = normalizeCartItemMeta(product, normalizedCalculatorData || {})
  const localCartItemId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  const confirmation = resolveCartConfirmationMeta(product, normalizedCalculatorData || {})

  return {
    cartItemId: localCartItemId,
    _id: productId,
    productId,
    product: productId,
    name: product.name || product.productName || product.fullName || '',
    fullName: product.fullName || product.name || product.productName || '',
    img: product.img || product.reducedImage || product.image || product.photo || '',
    qty,
    quantity: qty,
    purchasedQty: qty,
    category: product.category || product.categoryId || normalizedCalculatorData?.categoryKey || null,
    calculatorConfigured: normalizedCalculatorData?.configured === true,
    confirmationRequired: confirmation.confirmationRequired,
    itemConfirmed: confirmation.itemConfirmed,
    calculatorData: {
      ...(normalizedCalculatorData || {}),
      ...normalizedCartMeta,
      confirmationRequired: confirmation.confirmationRequired,
      itemConfirmed: confirmation.itemConfirmed,
      purchasedQty: qty,
    },
    ...normalizedCartMeta,
    color: normalizedCalculatorData?.color ?? '',
    meters: Number(normalizedCalculatorData?.meters || 0) || 0,
    pricePerMeter: Number(normalizedCalculatorData?.pricePerMeter || 0) || 0,
    description: normalizedCalculatorData?.description || '',
    mainImage: normalizedCalculatorData?.mainImage || product.img || product.reducedImage || '',
  }
}

const buildNextCartItemsAfterAdd = (items = [], product = {}, qty = 1, normalizedCalculatorData = null) => {
  const productId = getVisibleProductId(product)
  const currentItems = toArrayCartItems(items)
  const existed = findLocalExistingCartItem(currentItems, productId, normalizedCalculatorData)

  if (existed) {
    return currentItems.map((item) => {
      if (String(getLocalCartItemKey(item)) !== String(getLocalCartItemKey(existed))) return item

      const nextQty = getOrderItemQuantity(item) + qty
      const nextCalculatorData = normalizedCalculatorData
        ? { ...(item.calculatorData || {}), ...normalizedCalculatorData }
        : (item.calculatorData || {})
      const meta = normalizeCartItemMeta(product, nextCalculatorData)
      const confirmation = resolveCartConfirmationMeta(item, nextCalculatorData)

      return {
        ...item,
        qty: nextQty,
        quantity: nextQty,
        purchasedQty: nextQty,
        calculatorConfigured: nextCalculatorData?.configured === true,
        confirmationRequired: confirmation.confirmationRequired,
        itemConfirmed: confirmation.itemConfirmed,
        calculatorData: {
          ...nextCalculatorData,
          ...meta,
          confirmationRequired: confirmation.confirmationRequired,
          itemConfirmed: confirmation.itemConfirmed,
          purchasedQty: nextQty,
        },
        ...meta,
        color: nextCalculatorData?.color ?? item?.color ?? '',
        meters: Number(nextCalculatorData?.meters || item?.meters || 0) || 0,
        pricePerMeter: Number(nextCalculatorData?.pricePerMeter || item?.pricePerMeter || 0) || 0,
        description: nextCalculatorData?.description || item?.description || '',
        mainImage: nextCalculatorData?.mainImage || item?.mainImage || product?.img || product?.reducedImage || '',
      }
    })
  }

  return [
    ...currentItems,
    buildLocalCartItem(product, qty, normalizedCalculatorData),
  ]
}

const itemMatchesCartPayload = (item, payload = {}) => {
  const itemKey = getLocalCartItemKey(item)
  const itemProductId = getLocalProductId(item)
  const itemVariantKey = getOrderItemVariantKey(item)
  const itemSceneIdentity = getItemConfiguredSceneIdentity(item)
  const cartItemId = payload.cartItemId || payload.cartItemKey
  const productId = payload.productId
  const variantKey = payload.variantKey
  const payloadSceneIdentity = getConfiguredSceneIdentity(payload?.calculatorData || payload || {})

  if (cartItemId && String(itemKey) === String(cartItemId)) return true
  if (!cartItemId && payloadSceneIdentity && itemSceneIdentity === payloadSceneIdentity) return true
  if (!cartItemId && !payloadSceneIdentity && variantKey && String(itemVariantKey) === String(variantKey)) return true
  if (!cartItemId && !payloadSceneIdentity && !variantKey && productId && String(itemProductId) === String(productId)) return true

  return false
}

const findLocalExistingCartItem = (items, productId, calculatorData = null) => {
  const sameProduct = items.filter((item) => String(getLocalProductId(item)) === String(productId))
  if (!sameProduct.length) return null

  if (isConfiguredItem(calculatorData)) {
    const source = { productId, calculatorData }
    const incomingSceneIdentity = getConfiguredSceneIdentity(calculatorData)
    const variantKey = String(calculatorData?.variantKey || getOrderItemVariantKey(source) || '')
    const orderItemType = getOrderItemType(source)
    const workType = getOrderItemWorkType(source)
    const sizeLabel = getOrderItemSizeLabel(source)

    if (incomingSceneIdentity) {
      const exactSceneItem = sameProduct.find((item) => (
        getItemConfiguredSceneIdentity(item) === incomingSceneIdentity
      ))

      if (exactSceneItem) return exactSceneItem
    }

    const legacyCandidates = sameProduct.filter((item) => !getItemConfiguredSceneIdentity(item))

    return (
      legacyCandidates.find((item) => {
        const calc = item?.calculatorData || {}
        const itemVariantKey = String(calc?.variantKey || item?.variantKey || getOrderItemVariantKey(item) || '')

        if (variantKey && itemVariantKey) {
          return itemVariantKey === variantKey
        }

        return (
          getOrderItemType(item) === orderItemType &&
          getOrderItemWorkType(item) === workType &&
          getOrderItemSizeLabel(item) === sizeLabel
        )
      }) || null
    )
  }

  return (
    sameProduct.find((item) => {
      const calc = item?.calculatorData || {}
      return !item?.calculatorConfigured && calc?.configured !== true
    }) || null
  )
}

const buildCartPayloadItem = (product, qty, normalizedCalculatorData) => {
  const normalizedCartMeta = normalizeCartItemMeta(product, normalizedCalculatorData || {})

  return {
    product: product._id || product.productId || product.product,
    quantity: qty,
    calculatorData: {
      ...(normalizedCalculatorData || {}),
      ...normalizedCartMeta,
      configured: normalizedCalculatorData?.configured === true,
    },
    orderItemType: normalizedCartMeta.orderItemType,
    workType: normalizedCartMeta.workType,
    workKey: normalizedCartMeta.workKey,
    workName: normalizedCartMeta.workName,
    workLabel: normalizedCartMeta.workLabel,
    cartItemType: normalizedCartMeta.cartItemType,
    orderItemKind: normalizedCartMeta.orderItemKind,
    orderItemTypeLabel: normalizedCartMeta.orderItemTypeLabel,
    payablePrice: normalizedCartMeta.payablePrice,
    selectedPrice: normalizedCartMeta.selectedPrice,
    price: normalizedCartMeta.price,
    productPrice: normalizedCartMeta.productPrice,
    productBasePrice: normalizedCartMeta.productBasePrice,
    workPrice: normalizedCartMeta.workPrice,
    millingPrice: normalizedCartMeta.millingPrice,
    categoryName: normalizedCartMeta.categoryName,
    categoryLabel: normalizedCartMeta.categoryLabel,
    sizeLabel: normalizedCartMeta.sizeLabel,
    selectedSizeKey: normalizedCartMeta.selectedSizeKey,
    variantKey: normalizedCartMeta.variantKey,
    measureMode: normalizedCartMeta.measureMode,
    weightKg: normalizedCartMeta.weightKg,
    materialDensityKgM3: normalizedCartMeta.materialDensityKgM3,
    stoneDensityKgM3: normalizedCartMeta.stoneDensityKgM3,
  }
}


const getSessionCartItemsFromActiveSession = (session = {}) => {
  if (Array.isArray(session?.orderDraftState?.products)) return session.orderDraftState.products
  if (Array.isArray(session?.calc3dState?.cartItems)) return session.calc3dState.cartItems
  if (Array.isArray(session?.catalogState?.selectionItems)) return session.catalogState.selectionItems
  return []
}

const dispatchSessionCartSuccess = (dispatch, items, message = 'Корзина сессии обновлена', extra = {}) => {
  const normalized = toArrayCartItems(items)
  const cartContext = normalizeCartOperationContext(extra?.cartContext)

  dispatch({
    type: SESSION_CART_MUTATION_SUCCESS,
    payload: {
      ...extra,
      ...(cartContext?.scope === CART_SCOPE_SESSION
        ? {
            officeId: cartContext.officeId,
            sessionId: cartContext.sessionId,
            cartContext,
          }
        : {}),
      cartItems: normalized,
      updatedCart: normalized,
      message,
      isSessionCart: true,
      keepUpdating: extra.keepUpdating !== false,
    },
  })
}

export const updateCartItemQuantity = (payload, options = {}) => {
  return async (dispatch) => {
    const cartItemKey = getPayloadCartItemKey(payload)
    const cartContext = resolveCartOperationContext(options?.cartContext)

    try {
      dispatch({
        type: cartConstants.UPDATE_CART_ITEM_QUANTITY_REQUEST,
        payload: {
          ...payload,
          cartItemKey,
        },
      })

      if (cartContext.scope === CART_SCOPE_SESSION) {
        const quantity = Math.max(1, Number(payload?.quantity || payload?.qty || 1) || 1)
        const currentItems = getSessionCartItemsForContext(cartContext)

        if (!currentItems) {
          throw new Error('Контекст корзины сессии изменился. Обновление количества отменено.')
        }
        const nextItems = currentItems.map((item) => {
          if (!itemMatchesCartPayload(item, payload)) return item

          return {
            ...item,
            qty: quantity,
            quantity,
            purchasedQty: quantity,
            calculatorData: {
              ...(item.calculatorData || {}),
              purchasedQty: quantity,
            },
          }
        })

        dispatchSessionCartSuccess(
          dispatch,
          nextItems,
          'Количество товара в корзине сессии обновлено',
          { cartItemKey, cartContext }
        )

        const syncedSession = await syncActiveSalesSessionCart(nextItems, {
          source: 'session-cart-quantity',
          cartItemKey,
          cartContext,
        })

        if (!syncedSession) {
          throw new Error('Не удалось сохранить количество в корзине сессии')
        }

        return true
      }

      const { cart: { mainCartItems, cartItems }, auth: { authenticate } } = store.getState()

      if (authenticate) {
        const res = await axios.post('/user/cart/updateCartItemQuantity', payload)

        if (res.status === 200) {
          const { updatedCart, message } = res.data

          dispatch({
            type: cartConstants.UPDATE_CART_ITEM_QUANTITY_SUCCESS,
            payload: {
              updatedCart,
              message,
              cartItemKey,
            },
          })
        }
      } else {
        const items = toArrayCartItems(mainCartItems?.length ? mainCartItems : cartItems)
        const quantity = Math.max(1, Number(payload?.quantity ?? payload?.qty ?? 1) || 1)

        const nextItems = items.map((item) => {
          if (!itemMatchesCartPayload(item, payload)) return item

          return {
            ...item,
            qty: quantity,
            quantity,
            purchasedQty: quantity,
            calculatorData: {
              ...(item.calculatorData || {}),
              purchasedQty: quantity,
            },
          }
        })

        saveLocalCart(nextItems)

        dispatch({
          type: cartConstants.UPDATE_CART_ITEM_QUANTITY_SUCCESS,
          payload: {
            updatedCart: nextItems,
            message: 'Количество товара в корзине успешно обновлено',
            cartItemKey,
          },
        })
      }

      await dispatch(getCartItems({ silent: true, forceGlobal: true }))
      return true
    } catch (error) {
      dispatch({
        type: cartConstants.UPDATE_CART_ITEM_QUANTITY_FAILURE,
        payload: {
          cartItemKey,
          ...(cartContext.scope === CART_SCOPE_SESSION ? { cartContext } : {}),
          message: error?.response?.data?.message || 'Ошибка обновления количества товара в корзине',
        },
      })

      return false
    }
  }
}

const pendingSceneBlankSaves = new Set()

// Все текущие слоты оформления одного памятника сохраняются одним PATCH.
// Смена размера/товара в слоте заменяет строку, не добавляет второй портрет.
export const replaceSessionMonumentDecorations = ({ parentCartItemId, modelId, expectedCalculatorData, calculatorData, lines = [] }, cartContext) => async (dispatch) => {
  const context = normalizeCartOperationContext(cartContext)
  const pendingKey = `${context?.officeId}:${context?.sessionId}:${modelId}`
  if (context?.scope !== CART_SCOPE_SESSION) throw new Error('Нужна корзина конкретной сессии')
  const current = getSessionCartItemsForContext(context)
  if (!current) throw new Error('Сессия корзины изменилась')
  const matches = current.filter((item) => String(item.cartItemId || '') === String(parentCartItemId) && String(item.calculatorData?.modelId || '') === String(modelId))
  if (matches.length !== 1) throw new Error('Памятник сопоставился неоднозначно')
  const parent = matches[0]
  if (JSON.stringify(parent.calculatorData || {}) !== JSON.stringify(expectedCalculatorData || {})) throw new Error('Комплект изменился. Обновите бланк и повторите правку.')
  const belongs = (item) => {
    const calc = item.calculatorData || {}
    return calc.sceneItemType === 'decoration' && String(item.parentSceneObjectId || calc.parentSceneObjectId || '') === String(modelId)
      && (calc.source === 'monumentDecorations' || String(item.sceneObjectId || calc.sceneObjectId || '').startsWith(`monument-decoration:${modelId}:`))
  }
  const old = current.filter(belongs)
  const children = lines.filter((line) => line.productId && line.product && Number(line.quantity) > 0).map((line) => {
    const qty = Number(line.quantity) * getOrderItemQuantity(parent)
    const previous = old.find((item) => String(item.sceneObjectId || item.calculatorData?.sceneObjectId || '') === String(line.sceneObjectId))
    const unit = Math.round(Number(line.total || 0) / Number(line.quantity) * 1000000) / 1000000
    const product = { ...line.product, _id: line.productId, fullName: line.title, img: line.preview || '', price: unit }
    const calc = { ...line.calculatorData, productId: line.productId, unitPrice: unit, payablePrice: unit, itemConfirmed: true, confirmationRequired: false }
    return { ...buildLocalCartItem(product, qty, calc), ...(previous?.cartItemId ? { cartItemId: previous.cartItemId } : {}), price: unit, payablePrice: unit }
  })
  const next = current.filter((item) => !belongs(item)).map((item) => item === parent ? { ...item, calculatorData } : item).concat(children)
  const signature = (item) => JSON.stringify([String(item.productId?._id || item.productId || item._id || ''), getOrderItemQuantity(item), item.calculatorData?.payablePrice, item.calculatorData?.text, item.calculatorData?.variantKey, item.calculatorData?.surfaceWidthMm, item.calculatorData?.surfaceHeightMm])
  if (!pendingSceneBlankSaves.has(pendingKey) && JSON.stringify(parent.calculatorData) === JSON.stringify(calculatorData) && old.length === children.length && children.every((child) => old.some((item) => String(item.sceneObjectId || item.calculatorData?.sceneObjectId || '') === String(child.sceneObjectId || child.calculatorData?.sceneObjectId || '') && signature(item) === signature(child)))) return true
  pendingSceneBlankSaves.add(pendingKey)
  dispatchSessionCartSuccess(dispatch, next, 'Оформление памятника обновлено', { cartContext: context, silentAlert: true })
  const saved = await syncActiveSalesSessionCart(next, { cartContext: context, source: 'scene-blank', silentAlert: true })
  if (!saved) throw new Error('Не удалось сохранить оформление сессии. Повторите синхронизацию.')
  pendingSceneBlankSaves.delete(pendingKey)
  return true
}

export const updateCartItemConfig = (payload, options = {}) => {
  return async (dispatch) => {
    const cartItemKey = getPayloadCartItemKey(payload)
    const silentAlert = options?.silentAlert === true
    const cartContext = resolveCartOperationContext(options?.cartContext)

    try {
      dispatch({
        type: cartConstants.UPDATE_CART_ITEM_CONFIG_REQUEST,
        payload: {
          ...payload,
          cartItemKey,
        },
      })

      if (cartContext.scope === CART_SCOPE_SESSION) {
        const currentItems = getSessionCartItemsForContext(cartContext)
        const { calculatorData, quantity } = payload

        if (!currentItems) {
          throw new Error('Контекст корзины сессии изменился. Обновление конфигурации отменено.')
        }
        const matchedItems = currentItems.filter((item) => itemMatchesCartPayload(item, payload))

        if (matchedItems.length !== 1) {
          throw new Error(
            matchedItems.length === 0
              ? 'Позиция корзины сессии не найдена'
              : 'Конфигурация корзины сессии сопоставилась неоднозначно'
          )
        }

        console.log('[CART-TRACE][CONFIG][SESSION-MATCH]', {
          cartItemKey,
          requested: getCartTraceItem({
            cartItemId: payload?.cartItemId || payload?.cartItemKey || '',
            productId: payload?.productId || calculatorData?.productId || '',
            quantity: quantity ?? 1,
            calculatorData: calculatorData || {},
          }),
          matchedCount: matchedItems.length,
          matched: matchedItems.map(getCartTraceItem),
          cartCount: currentItems.length,
        })

        const nextItems = currentItems.map((item) => {
          if (!itemMatchesCartPayload(item, payload)) return item

          const nextQty = quantity !== undefined ? Math.max(1, Number(quantity) || 1) : getOrderItemQuantity(item)
          const nextCalculatorData = {
            ...(item.calculatorData || {}),
            ...(calculatorData || {}),
          }
          const meta = normalizeCartItemMeta(item, nextCalculatorData)
          const confirmation = resolveCartConfirmationMeta(item, nextCalculatorData)

          return {
            ...item,
            qty: nextQty,
            quantity: nextQty,
            purchasedQty: nextQty,
            calculatorConfigured: nextCalculatorData?.configured === true,
            confirmationRequired: confirmation.confirmationRequired,
            itemConfirmed: confirmation.itemConfirmed,
            calculatorData: {
              ...nextCalculatorData,
              ...meta,
              confirmationRequired: confirmation.confirmationRequired,
              itemConfirmed: confirmation.itemConfirmed,
              purchasedQty: nextQty,
            },
            ...meta,
            color: nextCalculatorData?.color ?? item?.color ?? '',
            meters: Number(nextCalculatorData?.meters || item?.meters || 0) || 0,
            pricePerMeter: Number(nextCalculatorData?.pricePerMeter || item?.pricePerMeter || 0) || 0,
            description: nextCalculatorData?.description || item?.description || '',
            mainImage: nextCalculatorData?.mainImage || item?.mainImage || '',
          }
        })

        dispatchSessionCartSuccess(
          dispatch,
          nextItems,
          'Конфигурация позиции корзины сессии обновлена',
          { cartItemKey, silentAlert, cartContext }
        )

        const syncedSession = await syncActiveSalesSessionCart(nextItems, {
          source: 'session-cart-config',
          silentAlert,
          cartItemKey,
          cartContext,
        })

        if (!syncedSession) {
          throw new Error('Не удалось сохранить корзину сессии')
        }

        return true
      }

      const { cart: { mainCartItems, cartItems }, auth: { authenticate } } = store.getState()

      if (authenticate) {
        const res = await axios.post('/user/cart/updateCartItemConfig', payload)

        if (res.status === 200) {
          const { updatedCart, message } = res.data

          dispatch({
            type: cartConstants.UPDATE_CART_ITEM_CONFIG_SUCCESS,
            payload: {
              updatedCart,
              message,
              cartItemKey,
              silentAlert,
            },
          })
        }
      } else {
        const items = toArrayCartItems(mainCartItems?.length ? mainCartItems : cartItems)
        const { cartItemId, calculatorData, quantity } = payload

        const nextItems = items.map((item) => {
          if (String(getLocalCartItemKey(item)) !== String(cartItemId)) return item

          const nextQty = quantity !== undefined
            ? Math.max(1, Number(quantity) || 1)
            : getOrderItemQuantity(item)
          const nextCalculatorData = {
            ...(item.calculatorData || {}),
            ...(calculatorData || {}),
          }
          const meta = normalizeCartItemMeta(item, nextCalculatorData)
          const confirmation = resolveCartConfirmationMeta(item, nextCalculatorData)

          return {
            ...item,
            qty: nextQty,
            quantity: nextQty,
            purchasedQty: nextQty,
            calculatorConfigured: nextCalculatorData?.configured === true,
            confirmationRequired: confirmation.confirmationRequired,
            itemConfirmed: confirmation.itemConfirmed,
            calculatorData: {
              ...nextCalculatorData,
              ...meta,
              confirmationRequired: confirmation.confirmationRequired,
              itemConfirmed: confirmation.itemConfirmed,
              purchasedQty: nextQty,
            },
            ...meta,
            color: nextCalculatorData?.color ?? '',
            meters: Number(nextCalculatorData?.meters || 0) || 0,
            pricePerMeter: Number(nextCalculatorData?.pricePerMeter || 0) || 0,
            description: nextCalculatorData?.description || '',
            mainImage: nextCalculatorData?.mainImage || '',
          }
        })

        saveLocalCart(nextItems)

        dispatch({
          type: cartConstants.UPDATE_CART_ITEM_CONFIG_SUCCESS,
          payload: {
            updatedCart: nextItems,
            message: 'Конфигурация позиции корзины успешно обновлена',
            cartItemKey,
            silentAlert,
          },
        })
      }

      await dispatch(getCartItems({ silent: true, forceGlobal: true }))
      return true
    } catch (error) {
      dispatch({
        type: cartConstants.UPDATE_CART_ITEM_CONFIG_FAILURE,
        payload: {
          cartItemKey,
          ...(cartContext.scope === CART_SCOPE_SESSION ? { cartContext } : {}),
          message: error?.response?.data?.message || 'Ошибка обновления конфигурации позиции корзины',
          silentAlert,
        },
      })

      return false
    }
  }
}

export const addToCart = (product, newQty = 1, type, calculatorData = null, options = {}) => {
  return async (dispatch) => {
    const silentAlert = options?.silentAlert === true
    const cartContext = resolveCartOperationContext(options?.cartContext)

    try {
      const qty = type === 'newQty' ? parseInt(newQty, 10) || 1 : parseInt(newQty, 10) || 1
      const normalizedCalculatorData = calculatorData && typeof calculatorData === 'object'
        ? calculatorData
        : null

      const useSalesSessionCart = cartContext.scope === CART_SCOPE_SESSION

      if (!useSalesSessionCart) {
        rememberConfiguredCartItem(product, normalizedCalculatorData || {})
      }

      if (useSalesSessionCart) {
        dispatch({ type: cartConstants.ADD_TO_CART_REQUEST })

        const currentItems = getSessionCartItemsForContext(cartContext)

        if (!currentItems) {
          throw new Error('Контекст корзины сессии изменился. Добавление отменено.')
        }

        const nextItems = buildNextCartItemsAfterAdd(currentItems, product, qty, normalizedCalculatorData)

        dispatchSessionCartSuccess(
          dispatch,
          nextItems,
          'Добавлено в корзину сессии',
          { silentAlert, cartContext }
        )

        const syncedSession = await syncActiveSalesSessionCart(nextItems, {
          source: 'session-cart-add',
          silentAlert,
          cartContext,
        })

        if (!syncedSession) {
          throw new Error('Не удалось сохранить корзину сессии')
        }

        return true
      }

      const { cart: { mainCartItems, cartItems }, auth } = store.getState()
      const currentItems = toArrayCartItems(mainCartItems?.length ? mainCartItems : cartItems)
      const normalizedCartMeta = normalizeCartItemMeta(product, normalizedCalculatorData || {})

      if (auth.authenticate) {
        dispatch({ type: cartConstants.ADD_TO_CART_REQUEST })

        const payload = {
          cartItems: [
            buildCartPayloadItem(product, qty, normalizedCalculatorData),
          ],
        }

        const res = await axios.post('/user/cart/addtocart', payload)

        if (res.status === 201) {
          dispatch({
            type: cartConstants.ADD_TO_CART_SUCCESS,
            payload: { message: res.data.message, cartItems: currentItems, silentAlert },
          })
        }

        await dispatch(getCartItems({ forceGlobal: true }))
      } else {
        const nextItems = buildNextCartItemsAfterAdd(currentItems, product, qty, normalizedCalculatorData)

        saveLocalCart(nextItems)

        dispatch({
          type: cartConstants.ADD_TO_CART_SUCCESS,
          payload: { cartItems: nextItems, message: 'Добавлено в корзину', silentAlert },
        })

        await dispatch(getCartItems({ forceGlobal: true }))
      }

      return true
    } catch (payload) {
      dispatch({
        type: cartConstants.ADD_TO_CART_FAILURE,
        payload: {
          ...(cartContext.scope === CART_SCOPE_SESSION ? { cartContext } : {}),
          message: payload?.response?.data?.message || 'Ошибка добавления в корзину',
          silentAlert,
        },
      })

      return false
    }
  }
}

export const getCartItems = (options = {}) => {
  return async (dispatch, getState) => {
    try {
      const silent = options?.silent === true
      const forceGlobal = options?.forceGlobal === true

      dispatch({
        type: cartConstants.GET_CART_ITEMS_REQUEST,
        payload: {
          silent,
        },
      })

      if (!forceGlobal && shouldUseSalesSessionCart()) {
        const identity = getActiveSalesSessionIdentity()
        const state = getState()
        const storedContext = state?.cart?.sessionCartContext || null
        const requestedContext = identity
          ? {
              scope: CART_SCOPE_SESSION,
              officeId: identity.officeId,
              sessionId: identity.sessionId,
            }
          : null
        const hasReadyMatchingSessionCart = state?.cart?.sessionCartReady === true &&
          isSameSessionCartContext(storedContext, requestedContext)
        const sessionItems = hasReadyMatchingSessionCart
          ? toArrayCartItems(state?.cart?.sessionCartItems || [])
          : getSessionCartItemsFromActiveSession(identity?.activeSession)

        dispatch({
          type: 'HYDRATE_CART_FROM_SALES_SESSION',
          payload: {
            officeId: identity?.officeId,
            sessionId: identity?.sessionId,
            cartItems: sessionItems,
          },
        })

        return sessionItems
      }

      const { auth: { authenticate } } = getState()

      if (authenticate) {
        const res = await axios.post('/user/getCartItems')
        if (res.status === 200) {
          const { cartItems } = res.data
          dispatch({
            type: cartConstants.GET_CART_ITEMS_SUCCESS,
            payload: { cartItems: mergeClientMetaIntoCartItems(Array.isArray(cartItems) ? cartItems : []) },
          })

          return cartItems || []
        }
      } else {
        const localCartItems = JSON.parse(localStorage.getItem('cart')) || []
        const normalized = mergeClientMetaIntoCartItems(Array.isArray(localCartItems) ? localCartItems : [])
        dispatch({
          type: cartConstants.GET_CART_ITEMS_SUCCESS,
          payload: { cartItems: normalized },
        })

        return normalized
      }

      return []
    } catch (error) {
      dispatch({
        type: cartConstants.GET_CART_ITEMS_FAILURE,
        payload: {
          error: error?.response?.data?.message || 'Ошибка получения корзины',
        },
      })

      return []
    }
  }
}

export const removeCartItem = (payload, options = {}) => {
  return async (dispatch) => {
    const normalizedPayload = payload && typeof payload === 'object'
      ? payload
      : { cartItemId: payload }
    const cartContext = resolveCartOperationContext(options?.cartContext)

    try {
      dispatch({ type: cartConstants.REMOVE_CART_ITEM_REQUEST })

      if (cartContext.scope === CART_SCOPE_SESSION) {
        const currentItems = getSessionCartItemsForContext(cartContext)

        if (!currentItems) {
          throw new Error('Контекст корзины сессии изменился. Удаление отменено.')
        }

        const updatedCartItems = currentItems.filter((item) => !itemMatchesCartPayload(item, normalizedPayload))

        dispatchSessionCartSuccess(
          dispatch,
          updatedCartItems,
          'Товар удалён из корзины сессии',
          { cartContext }
        )

        const syncedSession = await syncActiveSalesSessionCart(updatedCartItems, {
          source: 'session-cart-remove',
          cartContext,
        })

        if (!syncedSession) {
          throw new Error('Не удалось сохранить удаление в корзине сессии')
        }

        return true
      }

      const { cart: { mainCartItems, cartItems }, auth: { authenticate } } = store.getState()

      if (authenticate) {
        const res = await axios.post('/user/cart/removeItem', { payload: normalizedPayload })

        if (res.status === 202) {
          dispatch({
            type: cartConstants.REMOVE_CART_ITEM_SUCCESS,
            payload: {
              message: res.data.message,
            },
          })
          await dispatch(getCartItems({ forceGlobal: true }))
        } else {
          const { error } = res.data
          dispatch({
            type: cartConstants.REMOVE_CART_ITEM_FAILURE,
            payload: { error },
          })
        }
      } else {
        const items = toArrayCartItems(mainCartItems?.length ? mainCartItems : cartItems)

        const updatedCartItems = items.filter((item) => !itemMatchesCartPayload(item, normalizedPayload))

        saveLocalCart(updatedCartItems)

        dispatch({
          type: cartConstants.REMOVE_CART_ITEM_SUCCESS,
          payload: {
            message: 'Товар успешно удален с корзины!',
          },
        })

        await dispatch(getCartItems({ forceGlobal: true }))
      }

      return true
    } catch (error) {
      dispatch({
        type: cartConstants.REMOVE_CART_ITEM_FAILURE,
        payload: {
          ...(cartContext.scope === CART_SCOPE_SESSION ? { cartContext } : {}),
          message: error?.response?.data?.message || 'Что-то пошло не так!',
        },
      })

      return false
    }
  }
}

export const updateCart = () => {
  return async (dispatch) => {
    const { auth } = store.getState()
    const cartItems = localStorage.getItem('cart')
      ? JSON.parse(localStorage.getItem('cart'))
      : null

    if (auth.authenticate) {
      if (cartItems && Array.isArray(cartItems) && cartItems.length > 0) {
        const payload = {
          cartItems: cartItems.map((item) => {
            return buildCartPayloadItem(item, getOrderItemQuantity(item), item.calculatorData || {})
          }),
        }

        try {
          const res = await axios.post('/user/cart/addtocart', payload)

          if (res.status !== 201) {
            return false
          }

          // Локальную корзину удаляем только после подтверждённого переноса на сервер.
          localStorage.removeItem('cart')
          await dispatch(getCartItems({ forceGlobal: true }))
          return true
        } catch (error) {
          console.error('updateCart migration error:', error)
          // При сетевой/серверной ошибке локальная корзина остаётся источником данных.
          return false
        }
      }

      localStorage.removeItem('cart')
      await dispatch(getCartItems({ forceGlobal: true }))
      return true
    }

    if (cartItems) {
      dispatch({
        type: cartConstants.UPDATE_TO_CART_SUCCESS,
        payload: { cartItems },
      })
      await dispatch(getCartItems({ forceGlobal: true }))
    }

    return true
  }
}
